import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";

export type LineChartData = {
  title?: string | null;
  itemColor?: string;
  areaColor: string[];
  data?: number[] | string[];
};

type LineChartProps = {
  chartData: LineChartData[];
  title?: string;
  xAxisLabels: string[] | number[];
  height?: string;
  width?: string;
};

const LineChart: React.FC<LineChartProps> = ({
  title,
  chartData,
  height = "400px",
  width = "100%",
  xAxisLabels,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<echarts.EChartsType | null>(null);
  const [theme, setTheme] = useState<string | null>(
    document.documentElement.getAttribute("data-theme")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const newTheme = document.documentElement.getAttribute("data-theme");
      setTheme(newTheme);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const isLightTheme = theme === "light";

    chartInstance.current.setOption({
      background: "transparent",
      title: {
        text: title,
        textStyle: {
          color: isLightTheme ? "#000" : "#fff",
          fontSize: "0.8rem",
        },
        left: "left",
      },
      legend: {
        data: chartData.map(({ title }) => ({
          name: title,
          icon: "circle",
        })),
        right: "3%",
        align: "left",
        textStyle: {
          color: isLightTheme ? "#000" : "#fff",
        },
        itemGap: 20,
      },
      grid: {
        left: 10,
        right: 10,
        top: 40,
        bottom: 0,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        textStyle: {
          color: "#fff",
        },
        formatter: (params: any) => {
          const xValue = params[0].name;
          let tooltipContent = `${xValue}<br/>`;
          params.forEach((item: any) => {
            tooltipContent += `${item.seriesName}: ${item.value}<br/>`;
          });
          return tooltipContent;
        },
      },
      xAxis: {
        type: "category",
        data: xAxisLabels,
        axisLabel: {
          color: isLightTheme ? "#000" : "#ccc",
        },
        axisLine: {
          lineStyle: {
            color: isLightTheme ? "#000" : "#ccc",
          },
        },
        boundaryGap: false,
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
        max:
          Math.max(
            ...chartData.flatMap(({ data }) => (data || []).map(Number))
          ) + 2,
        axisLabel: {
          color: isLightTheme ? "#000" : "#ccc",
        },
        axisLine: {
          lineStyle: {
            color: isLightTheme ? "#000" : "#ccc",
          },
        },
        axisTick: {
          inside: true,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: "rgba(128, 128, 128, 0.5)",
            type: "dashed",
          },
        },
      },
      series: chartData.map(({ title, data, itemColor, areaColor }) => ({
        name: title,
        type: "line",
        smooth: true,
        symbolSize: 0,
        sampling: "average",
        itemStyle: {
          color: itemColor,
        },
        stack: "a",
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: areaColor[0] },
            { offset: 1, color: areaColor[1] },
          ]),
        },
        data,
      })),
    });
  }, [chartData, title, xAxisLabels, theme]);

  return <div ref={chartRef} style={{ width, height }} />;
};

export default LineChart;
